import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  Users, 
  MessageCircle, 
  Github, 
  Twitter, 
  Linkedin,
  ExternalLink,
  Star,
  Fork,
  Eye
} from 'lucide-react'

const Community: React.FC = () => {
  const communityStats = [
    { icon: Users, label: '开发者', value: '2,500+', color: 'from-blue-500 to-cyan-500' },
    { icon: MessageCircle, label: '讨论', value: '15,000+', color: 'from-green-500 to-emerald-500' },
    { icon: Github, label: '开源项目', value: '50+', color: 'from-purple-500 to-pink-500' },
    { icon: Star, label: 'GitHub Stars', value: '10,000+', color: 'from-yellow-500 to-orange-500' }
  ]

  const platforms = [
    {
      name: 'GitHub',
      description: '开源代码和项目协作',
      icon: Github,
      url: 'https://github.com/deeplink',
      color: 'from-gray-800 to-gray-900',
      stats: { repos: 50, stars: 10000, forks: 2500 }
    },
    {
      name: 'Discord',
      description: '实时聊天和技术讨论',
      icon: MessageCircle,
      url: 'https://discord.gg/deeplink',
      color: 'from-indigo-500 to-purple-500',
      stats: { members: 5000, online: 500 }
    },
    {
      name: 'Twitter',
      description: '最新动态和产品更新',
      icon: Twitter,
      url: 'https://twitter.com/deeplink',
      color: 'from-blue-400 to-cyan-400',
      stats: { followers: 25000, tweets: 1000 }
    },
    {
      name: 'LinkedIn',
      description: '专业网络和招聘信息',
      icon: Linkedin,
      url: 'https://linkedin.com/company/deeplink',
      color: 'from-blue-600 to-blue-700',
      stats: { followers: 10000, employees: 100 }
    }
  ]

  const recentActivities = [
    {
      type: 'discussion',
      title: 'CoreTrust SDK v1.2.0 发布讨论',
      author: 'DeepLink团队',
      time: '2小时前',
      replies: 15,
      likes: 32
    },
    {
      type: 'issue',
      title: '钱包连接问题修复',
      author: '开发者张三',
      time: '4小时前',
      replies: 8,
      likes: 12
    },
    {
      type: 'feature',
      title: '新功能：支持更多区块链网络',
      author: '产品经理李四',
      time: '1天前',
      replies: 25,
      likes: 45
    },
    {
      type: 'tutorial',
      title: '如何集成CoreTrust到React应用',
      author: '技术专家王五',
      time: '2天前',
      replies: 12,
      likes: 28
    }
  ]

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'discussion': return MessageCircle
      case 'issue': return Github
      case 'feature': return Star
      case 'tutorial': return ExternalLink
      default: return MessageCircle
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'discussion': return 'text-blue-500'
      case 'issue': return 'text-red-500'
      case 'feature': return 'text-green-500'
      case 'tutorial': return 'text-purple-500'
      default: return 'text-gray-500'
    }
  }

  return (
    <>
      <Helmet>
        <title>开发者社区 - DeepLink Limited</title>
        <meta name="description" content="加入DeepLink开发者社区，与其他开发者交流技术，分享经验，共同构建Web3生态。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                开发者社区
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                加入我们的开发者社区，与其他开发者交流技术，分享经验，共同构建Web3生态
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://github.com/deeplink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center"
                >
                  <Github className="w-5 h-5 mr-2" />
                  查看GitHub
                </a>
                <a
                  href="https://discord.gg/deeplink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300 flex items-center justify-center"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  加入Discord
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {communityStats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                社区平台
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                选择您喜欢的平台加入我们的社区
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {platforms.map((platform, index) => {
                const Icon = platform.icon
                return (
                  <motion.a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-r ${platform.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      {platform.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {platform.description}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(platform.stats).map(([key, value]) => (
                        <div key={key} className="text-center">
                          <div className="text-lg font-bold text-gray-900 dark:text-white">
                            {typeof value === 'number' ? value.toLocaleString() : value}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-500 capitalize">
                            {key}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.a>
                )
              })}
            </div>
          </div>
        </section>

        {/* Recent Activities */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                最新动态
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                社区中的最新讨论和活动
              </p>
            </motion.div>

            <div className="space-y-6">
              {recentActivities.map((activity, index) => {
                const Icon = getActivityIcon(activity.type)
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-10 h-10 ${getActivityColor(activity.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                          {activity.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                          <span>作者：{activity.author}</span>
                          <span>时间：{activity.time}</span>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {activity.replies} 回复
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {activity.likes} 点赞
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                准备加入我们的社区？
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                无论您是开发者、设计师还是Web3爱好者，我们都欢迎您的加入
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://github.com/deeplink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
                >
                  <Github className="w-5 h-5 mr-2" />
                  开始贡献代码
                </a>
                <a
                  href="https://discord.gg/deeplink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition-colors flex items-center justify-center"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  参与讨论
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Community